import React, { createContext, useContext, ReactNode, useState } from 'react';
import { ImprovementType } from 'components/Editor/AIAssist/types';

export interface AIGenerationParams {
  text: string;
  type: ImprovementType;
  userPrompt?: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

interface AIHook {
  data: string;
  error: string | null;
  isLoading: boolean;
  startGeneration: (params: AIGenerationParams) => void;
  stop: () => void;
  reset: () => void;
}

interface AIGenerationContextValue {
  generate: (params: AIGenerationParams) => void;
  reset: () => void;
  stop: () => void;
  data: string;
  error: string | null;
  isLoading: boolean;
  currentAction: ImprovementType | undefined;
  hasData: boolean;
  hasError: boolean;
  trackingContext?: string;
  onPauseForSession?: () => void;
  onPauseUntilReactivate?: () => void;
}

const AIGenerationContext = createContext<AIGenerationContextValue | null>(
  null,
);

interface Props {
  aiHook?: AIHook;
  children: ReactNode;
  trackingContext?: string;
  onPauseForSession?: () => void;
  onPauseUntilReactivate?: () => void;
}

export function AIGenerationProvider({
  aiHook,
  children,
  trackingContext,
  onPauseForSession,
  onPauseUntilReactivate,
}: Props) {
  const [currentAction, setCurrentAction] = useState<
    ImprovementType | undefined
  >();

  function generate(params: AIGenerationParams) {
    if (!aiHook) return;
    setCurrentAction(params.type);
    aiHook.startGeneration(params);
  }

  function reset() {
    setCurrentAction(undefined);
    aiHook?.reset();
  }

  function stop() {
    aiHook?.stop();
  }

  const value: AIGenerationContextValue = {
    generate,
    reset,
    stop,
    data: aiHook?.data ?? '',
    error: aiHook?.error ?? null,
    isLoading: aiHook?.isLoading ?? false,
    currentAction,
    hasData: !!aiHook?.data,
    hasError: !!aiHook?.error,
    trackingContext,
    onPauseForSession,
    onPauseUntilReactivate,
  };

  return (
    <AIGenerationContext.Provider value={value}>
      {children}
    </AIGenerationContext.Provider>
  );
}

export function useAIGenerationContext() {
  const context = useContext(AIGenerationContext);
  if (!context) {
    throw new Error(
      'useAIGenerationContext must be used within AIGenerationProvider',
    );
  }
  return context;
}
